/* Correcting a refuel the driver logged wrong — without losing what they said.
 *
 * A driver at a pump types a shilling amount and reads litres off a receipt.
 * Sometimes one of those is wrong: 4 L for 40 L, a cost that includes the oil
 * they also bought, the wrong bus. Today the office's only options are to live
 * with the bad row or to have nobody touch it, and both of those poison the
 * numbers that fuel theft detection is built on.
 *
 * WHY THE ORIGINAL IS KEPT
 * -----------------------
 * An admin who can silently rewrite what a driver reported is exactly the hole
 * this product exists to close. A correction that erases the original turns the
 * refuel log from evidence into an opinion — and the one scenario worth
 * designing against is a manager quietly editing away the fill they siphoned.
 * So a correction is ADDITIVE: the corrected figures go on the row (because
 * every aggregation in the dashboard already reads `litersAdded` and
 * `costEstimate`, and a fix nothing reads is not a fix), and the driver's
 * original is copied to `original` on the FIRST correction and never touched
 * again. Correct a row twice and you still see what the driver first said.
 *
 * NO RULE CHANGE NEEDED: fleet admins can already update these documents.
 *
 * THE ODOMETER (2026-10-28, the console's "Correct the refill")
 * ------------------------------------------------------------
 * A flagged reading — lower than the fill before, or a jump no bus could drive —
 * can be restated too. A reading is also a distance: the server's per-fill
 * economy reads `kmSinceLastRefuel` on this fill and on the next one, so both
 * are restated from the corrected reading, and when this is the bus's newest
 * reading the bus's own `odometerKm` follows (a fleet admin may write it with
 * the fuel details). The driver's reading joins `original` like the rest.
 */

import { doc, getDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { db, auth } from '../firebase';

/** Which collection the row lives in — buses vs lorries. */
export type RefuelCollection = 'busRefuelEvents' | 'fleetRefuelEvents';

export interface RefuelCorrection {
  /** Litres actually put in. Undefined leaves the logged value alone. */
  litersAdded?: number;
  /** Shillings actually paid. Undefined leaves the logged value alone. */
  costEstimate?: number;
  /** The dash reading at this fill. Undefined leaves the logged value alone. */
  odometerKm?: number;
  /** Why — shown beside the correction so it is never an unexplained edit. */
  reason: string;
}

export class RefuelCorrectionError extends Error {}

/* The same band the mobile price learner uses. A correction is a chance to fix
   a typo, not a second chance to make one. */
const MIN_LITRES = 0.5;
const MAX_LITRES = 2000;
const MIN_COST = 1;
const MAX_COST = 2_000_000;
const MAX_ODOMETER_KM = 3_000_000;

/** The fills either side of the one being corrected, for its distances. */
export interface OdometerNeighbours {
  /** The reading at the fill before that has one. */
  prevOdometerKm?: number | null;
  /** The next fill with a reading: its distance is measured from this one. */
  next?: { id: string; odometerKm: number } | null;
  /** Set when this fill holds the bus's newest reading: the bus follows. */
  busId?: string | null;
}

/**
 * Apply a correction, preserving what the driver originally reported.
 *
 * Throws rather than failing quietly: this is invoked from a button an admin
 * pressed on purpose, and silence would leave them believing a bad number was
 * fixed when it was not.
 */
export async function correctRefuelEvent(
  eventId: string,
  correction: RefuelCorrection,
  collectionName: RefuelCollection = 'busRefuelEvents',
  neighbours: OdometerNeighbours = {},
): Promise<void> {
  const reason = (correction.reason || '').trim();
  if (!reason) {
    throw new RefuelCorrectionError('Say why this is being corrected.');
  }
  if (correction.litersAdded === undefined && correction.costEstimate === undefined && correction.odometerKm === undefined) {
    throw new RefuelCorrectionError('Nothing to change — set the litres, the cost or the odometer.');
  }
  if (
    correction.litersAdded !== undefined &&
    (!Number.isFinite(correction.litersAdded) ||
      correction.litersAdded < MIN_LITRES ||
      correction.litersAdded > MAX_LITRES)
  ) {
    throw new RefuelCorrectionError(`Litres must be between ${MIN_LITRES} and ${MAX_LITRES}.`);
  }
  if (
    correction.costEstimate !== undefined &&
    (!Number.isFinite(correction.costEstimate) ||
      correction.costEstimate < MIN_COST ||
      correction.costEstimate > MAX_COST)
  ) {
    throw new RefuelCorrectionError('That cost does not look like a real amount.');
  }
  const odo = correction.odometerKm;
  if (odo !== undefined) {
    if (!Number.isFinite(odo) || odo <= 0 || odo > MAX_ODOMETER_KM) {
      throw new RefuelCorrectionError('That odometer reading does not look real.');
    }
    if (neighbours.prevOdometerKm != null && odo < neighbours.prevOdometerKm) {
      throw new RefuelCorrectionError(`Lower than the reading at the fill before (${Math.round(neighbours.prevOdometerKm).toLocaleString()} km).`);
    }
    if (neighbours.next && odo > neighbours.next.odometerKm) {
      throw new RefuelCorrectionError(`Higher than the reading at the fill after (${Math.round(neighbours.next.odometerKm).toLocaleString()} km).`);
    }
  }

  const ref = doc(db, collectionName, eventId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    throw new RefuelCorrectionError('That refuel is no longer there.');
  }
  const data = snap.data() as Record<string, unknown>;

  if (data.voided === true) {
    // A driver already took this one back. Correcting a cancelled row would
    // resurrect a refuel nobody claims happened.
    throw new RefuelCorrectionError(
      'That refuel was already cancelled by the driver, so there is nothing to correct.',
    );
  }

  /* Written ONCE. A second correction must still show what the driver said, not
     what the last admin said — otherwise two edits launder the original away
     one step at a time, which is the same erasure this is meant to prevent. */
  const alreadyCorrected = data.corrected === true && data.original !== undefined;
  const original = alreadyCorrected
    ? undefined
    : {
        litersAdded: (data.litersAdded as number | undefined) ?? null,
        costEstimate: (data.costEstimate as number | undefined) ?? null,
        litersUnknown: (data.litersUnknown as boolean | undefined) ?? false,
        costUnknown: (data.costUnknown as boolean | undefined) ?? false,
        fuelLevelAfterRefuel: (data.fuelLevelAfterRefuel as string | undefined) ?? null,
        odometerKm: (data.odometerKm as number | undefined) ?? null,
        kmSinceLastRefuel: (data.kmSinceLastRefuel as number | undefined) ?? null,
      };

  const batch = writeBatch(db);
  const distance = (from: number | null | undefined, to: number) => (from != null && to > from ? Math.round((to - from) * 10) / 10 : null);
  batch.update(ref, {
    ...(correction.litersAdded !== undefined
      ? { litersAdded: correction.litersAdded, litersUnknown: false }
      : {}),
    ...(correction.costEstimate !== undefined
      ? { costEstimate: correction.costEstimate, costUnknown: false }
      : {}),
    // The distance only when the fill before has a reading: with none, the
    // logged distance came from somewhere else and stays.
    ...(odo !== undefined ? { odometerKm: odo } : {}),
    ...(odo !== undefined && neighbours.prevOdometerKm != null
      ? { kmSinceLastRefuel: distance(neighbours.prevOdometerKm, odo) }
      : {}),
    ...(original ? { original } : {}),
    /* A row corrected before readings could be (litres and cost only) has an
       `original` without the reading. The driver's reading is added to it as
       a key that was never there — nothing already kept is rewritten. */
    ...(alreadyCorrected && odo !== undefined && (data.original as Record<string, unknown>).odometerKm === undefined
      ? {
        'original.odometerKm': (data.odometerKm as number | undefined) ?? null,
        'original.kmSinceLastRefuel': (data.kmSinceLastRefuel as number | undefined) ?? null,
      }
      : {}),
    corrected: true,
    correctionReason: reason.slice(0, 300),
    correctedAt: serverTimestamp(),
    correctedBy: auth.currentUser?.uid || '',
    /* Corrections are the office answering the question the flag asked, so the
       row stops sitting in the "waiting on an admin" bucket. */
    awaitingAdminConfirmation: false,
  });
  if (odo !== undefined && neighbours.next) {
    batch.update(doc(db, collectionName, neighbours.next.id), { kmSinceLastRefuel: distance(odo, neighbours.next.odometerKm) });
  }
  if (odo !== undefined && neighbours.busId && collectionName === 'busRefuelEvents') {
    batch.update(doc(db, 'buses', neighbours.busId), { odometerKm: odo, updatedAt: serverTimestamp() });
  }
  await batch.commit();
}

/** What the driver first reported, when a row has been corrected since. */
export interface OriginalRefuel {
  litersAdded: number | null;
  costEstimate: number | null;
  fuelLevelAfterRefuel?: string | null;
  odometerKm?: number | null;
}

export function originalOf(data: Record<string, unknown> | undefined): OriginalRefuel | null {
  if (!data || data.corrected !== true) return null;
  const o = data.original as Record<string, unknown> | undefined;
  if (!o) return null;
  return {
    litersAdded: typeof o.litersAdded === 'number' ? o.litersAdded : null,
    costEstimate: typeof o.costEstimate === 'number' ? o.costEstimate : null,
    fuelLevelAfterRefuel:
      typeof o.fuelLevelAfterRefuel === 'string' ? o.fuelLevelAfterRefuel : null,
    odometerKm: typeof o.odometerKm === 'number' ? o.odometerKm : null,
  };
}

/**
 * Alert types that mean "these fuel figures disagree with something".
 *
 * Deliberately a short list. The agreed shape (2026-07-30) is that correcting a
 * refuel appears where a contradiction has been flagged — NOT as a free edit
 * button on every row. An always-available edit control invites tidying the
 * numbers until they look right, which is indistinguishable from covering a
 * loss; an edit that only opens on a flagged contradiction leaves a trail of
 * why anyone touched it.
 */
const CONTRADICTION_TYPES = new Set([
  'fuel_needs_review',
  'fuel_efficiency_anomaly',
  'refuel_risk',
  'frequent_liters_unknown',
  'refuel_awaiting_admin',
  'fuel_theft',
  'missing_refuel',
]);

export function alertInvitesRefuelCorrection(type: string | undefined): boolean {
  return CONTRADICTION_TYPES.has((type || '').trim());
}
